import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "/pwa-512x512.png";
import { useAuth } from "@/contexts/AuthContext";
import { API_URL } from "@/services/apiClient";
import { firebaseMessagingService } from "@/config/firebase";

type AuthView = "sign-in" | "sign-up" | "forgot-password";

export function UnifiedLogin() {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();

  const [view, setView] = useState<AuthView>("sign-in");
  const [isSignUp, setIsSignUp] = useState(false);

  // Sign-in state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Forgot password state
  const [resetEmail, setResetEmail] = useState("");

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Password change state (for school admins with temp passwords)
  const [requirePasswordChange, setRequirePasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [tempEmail, setTempEmail] = useState("");
  const [tempPassword, setTempPassword] = useState("");

  // Sync the sliding panel animation with view state
  useEffect(() => {
    const container = document.querySelector(".auth-container");
    if (!container) return;
    if (isSignUp) container.classList.add("sign-up-mode");
    else container.classList.remove("sign-up-mode");
  }, [isSignUp]);

  // Switch views
  const switchToSignUp = () => {
    setView("sign-up");
    setIsSignUp(true);
    setError("");
    setSuccess("");
  };

  const switchToSignIn = () => {
    setView("sign-in");
    setIsSignUp(false);
    setError("");
    setSuccess("");
  };

  // const switchToForgotPassword = () => {
  //   setView("forgot-password");
  //   setIsSignUp(true);
  //   setError("");
  //   setSuccess("");
  // };

  /**
   * Redirect to the appropriate dashboard based on the user's role.
   */
  const redirectByRole = (role: string) => {
    switch (role) {
      case "super_admin":
        navigate("/system-admin");
        break;
      case "admin":
        navigate("/admin");
        break;
      case "teacher":
        navigate("/teacher/dashboard");
        break;
      case "student":
        navigate("/student/dashboard");
        break;
      default:
        navigate("/dashboard");
    }
  };

  /**
   * Store auth data in localStorage after successful login.
   */
  const storeAuth = (
    token: string,
    role: string,
    user: Record<string, unknown>,
    refreshToken?: string
  ) => {
    sessionStorage.setItem("authToken", token);
    localStorage.setItem("userRole", role);
    localStorage.setItem("user", JSON.stringify(user));
    if (refreshToken) {
      sessionStorage.setItem("refreshToken", refreshToken);
    }
    if (user.authUid) {
      localStorage.setItem("authUid", user.authUid as string);
    }
    if (user.id) {
      localStorage.setItem("userId", user.id as string);
    }
    if (user.schoolId) {
      localStorage.setItem("schoolId", user.schoolId as string);
    }
  };

  /**
   * Unified sign-in: single /auth/user/login handles students, teachers, and
   * school admins. System admin falls back to /auth/system-admin/login.
   */
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      // 1. Try unified login — handles students, teachers, and school admins
      let response = await fetch(`${API_URL}/auth/user/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ email, password }),
      });
      let data = await response.json();

      if (response.ok) {
        // School admin with temp password → password change flow
        if (data.requirePasswordChange) {
          setRequirePasswordChange(true);
          setTempEmail(email);
          setTempPassword(password);
          return;
        }

        if (data.token) {
          const role = data.user?.role === 'admin' ? 'admin' : (data.user?.role || "student");
          storeAuth(data.token, role, data.user, data.refreshToken);
          if (data.user?.authUid && role !== "student") {
            firebaseMessagingService
              .initializeForUser(data.user.authUid)
              .catch(() => {});
          }
          redirectByRole(role);
          return;
        }
      }

      // 2. Fallback: system admin login (separate Redis-based auth)
      response = await fetch(`${API_URL}/auth/system-admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ email, password }),
      });
      data = await response.json();

      if (response.ok && data.token) {
        storeAuth(data.token, "super_admin", data.user);
        redirectByRole("super_admin");
        return;
      }

      // All attempts failed
      const errMsg = typeof data.error === 'string' ? data.error : (data.error?.message || data.message || 'Invalid email or password. Please try again.');
      setError(errMsg);
    } catch (_err) {
      setError("Failed to connect to server. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle setting a new password for school admins with temp passwords.
   */
  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/school-admin/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: tempEmail,
          tempPassword,
          newPassword,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        const errMsg = typeof data.error === 'string' ? data.error : (data.error?.message || data.message || 'Failed to set password');
        setError(errMsg);
        return;
      }

      storeAuth(data.token, "admin", data.user, data.refreshToken);
      redirectByRole("admin");
    } catch (_err) {
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Forgot password flow.
   */
  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail }),
      });
      const data = await response.json();

      if (!response.ok) {
        const errMsg = typeof data.error === 'string' ? data.error : (data.error?.message || data.message || 'Failed to send reset email.');
        setError(errMsg);
        return;
      }

      setSuccess("Password reset instructions sent to your email.");
      setTimeout(() => {
        switchToSignIn();
        setResetEmail("");
      }, 3000);
    } catch (_err) {
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Google SSO login.
   */
  const handleGoogleLogin = async () => {
    setError("");
    setLoading(true);
    try {
      await loginWithGoogle();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Google login failed.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generic SSO login (for other providers like Microsoft, SAML, etc.).
   */
  const handleSSOLogin = async () => {
    setError("");
    setLoading(true);
    try {
      // For now, redirect to a generic SSO endpoint or show a message
      // This could be extended to support Microsoft, SAML, or other providers
      const response = await fetch(`${API_URL}/auth/sso/providers`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.providers && data.providers.length > 0) {
          // H9: Validate SSO redirect URL against trusted identity provider domains
          const allowedSSOPrefixes = [
            'https://accounts.google.com/',
            'https://login.microsoftonline.com/',
            'https://login.microsoft.com/',
            `${window.location.origin}/`,
          ];
          const authUrl: string = data.providers[0].authUrl || '';
          if (!allowedSSOPrefixes.some((prefix: string) => authUrl.startsWith(prefix))) {
            setError('Untrusted SSO redirect URL. Please contact your administrator.');
            return;
          }
          window.location.href = authUrl;
        } else {
          setError("No SSO providers configured. Please contact your administrator.");
        }
      } else {
        // Fallback: show message about SSO configuration
        setError("SSO login is not configured. Please use email/password or Google login.");
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "SSO login failed.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // --- Password change screen for school admins ---
  if (requirePasswordChange) {
    return (
      <>
        <AuthStyles />
        <div className="auth-page">
          <div className="auth-container" style={{ maxWidth: 460 }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: "3rem 2.5rem",
                height: "100%",
              }}
            >
              <div className="auth-logo">
                <img src={logo} alt="ShieldED Haven" className="auth-logo-img" />
              </div>
              <h2 className="title">Set Your Password</h2>
              <p
                style={{
                  color: "#666",
                  fontSize: "0.9rem",
                  marginBottom: 16,
                  textAlign: "center",
                }}
              >
                Create a new password to secure your account
              </p>

              {error && <div className="auth-error">{error}</div>}

              <form onSubmit={handleSetPassword} style={{ width: "100%" }}>
                <div className="input-field">
                  <i>🔒</i>
                  <input
                    type="password"
              autoComplete="current-password"
                    placeholder="New Password (min 8 chars)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                  />
                </div>
                <div className="input-field">
                  <i>🔒</i>
                  <input
                    type="password"
              autoComplete="current-password"
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                <input
                  type="submit"
                  value={loading ? "Setting..." : "Set Password & Continue"}
                  className="btn solid"
                  disabled={loading}
                />
              </form>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AuthStyles />

      <div className="auth-page">
        <div className="auth-container">
          <div className="forms-container">
            <div className="signin-signup">
              {/* ===== SIGN IN FORM ===== */}
              <form className="sign-in-form" onSubmit={handleSignIn}>
                <div className="auth-logo">
                  <img src={logo} alt="ShieldED Haven" className="auth-logo-img" />
                </div>
                <h5>Welcome to</h5>
                <h5 className="title">ShieldEd Haven</h5>

                {error && view === "sign-in" && (
                  <div className="auth-error">{error}</div>
                )}
                {success && view === "sign-in" && (
                  <div className="auth-success">{success}</div>
                )}

                <div className="input-field">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="input-field">
                  <input
                    type="password"
              autoComplete="current-password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>

                <Link className="forgot-link" to="/forgot-password">
                  Forgot your password?
                </Link>

                <input
                  type="submit"
                  value={loading ? "Signing In..." : "Sign In"}
                  className="btn solid"
                  disabled={loading}
                />

                <p className="social-text">Or sign in with</p>
                <div className="social-media">
                  <button
                    type="button"
                    className="google-signin-btn"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                  >
                    <GoogleIcon />
                    <span>Google</span>
                  </button>
                  <button
                    type="button"
                    className="sso-signin-btn"
                    onClick={handleSSOLogin}
                    disabled={loading}
                  >
                    <SSOIcon />
                    <span>SSO</span>
                  </button>
                </div>
              </form>

              {/* ===== SIGN UP / FORGOT PASSWORD FORM ===== */}
              <form
                className="sign-up-form"
                onSubmit={
                  view === "forgot-password"
                    ? handleForgotPassword
                    : (e) => e.preventDefault()
                }
              >
                <div className="auth-logo" style={{ alignSelf: "flex-start" }}>
                  <img src={logo} alt="ShieldED Haven" className="auth-logo-img" />
                </div>

                {view === "forgot-password" ? (
                  <>
                    <h2 className="title">Reset Password</h2>

                    {error && <div className="auth-error">{error}</div>}
                    {success && <div className="auth-success">{success}</div>}

                    <div className="input-field">
                      <i>📧</i>
                      <input
                        type="email"
                        placeholder="Your email address"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        required
                      />
                    </div>

                    <input
                      type="submit"
                      value={loading ? "Sending..." : "Send Reset Link"}
                      className="btn solid"
                      disabled={loading}
                    />

                    <button
                      type="button"
                      className="forgot-link"
                      onClick={switchToSignIn}
                      style={{ marginTop: 12 }}
                    >
                      Back to Sign In
                    </button>
                  </>
                ) : (
                  <>
                    <h2 className="title" style={{ textAlign: "left", alignSelf: "flex-start" }}>Access by Invitation</h2>
                    <p style={{ color: "#aaa", fontSize: "0.82rem", marginBottom: "2rem", textAlign: "left", lineHeight: 1.6, alignSelf: "flex-start", width: "100%" }}>
                      ShieldED Haven is a closed platform.<br />Access is granted by invitation only.
                    </p>

                    {/* Row: Student / Teacher */}
                    <div style={{ width: "100%", marginBottom: "1.5rem", textAlign: "left", alignSelf: "flex-start" }}>
                      <p style={{ margin: "0 0 0.25rem", fontSize: "0.78rem", fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Student or Teacher
                      </p>
                      <p style={{ margin: 0, color: "#666", fontSize: "0.85rem", lineHeight: 1.6 }}>
                        Your school administrator manages access. Check your email for an invitation, or contact your school office to be added.
                      </p>
                    </div>

                    {/* Divider */}
                    <div style={{ width: "100%", borderTop: "1px solid #eee", marginBottom: "1.5rem" }} />

                    {/* Row: School wanting to join */}
                    <div style={{ width: "100%", marginBottom: "2rem", textAlign: "left", alignSelf: "flex-start" }}>
                      <p style={{ margin: "0 0 0.25rem", fontSize: "0.78rem", fontWeight: 700, color: "#bbb", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        School Administrator
                      </p>
                      <p style={{ margin: "0 0 0.75rem", color: "#666", fontSize: "0.85rem", lineHeight: 1.6 }}>
                        Want to bring ShieldED Haven to your institution? Contact our team to get your school set up.
                      </p>
                      <a
                        href="mailto:support@shieldedhaven.com"
                        style={{
                          fontSize: "0.82rem",
                          fontWeight: 600,
                          color: "#5c6bc0",
                          textDecoration: "none",
                        }}
                      >
                        contact us
                      </a>
                    </div>

                   
                  </>
                )}
              </form>
            </div>
          </div>

          {/* ===== SLIDING PANELS ===== */}
          <div className="panels-container">
            <div className="panel left-panel">
              <div className="content">
                <h3>New here?</h3>
                <p>
                  Have an invite from your school? Create your account and join
                  ShieldED Haven in seconds.
                </p>
                <button className="btn transparent" onClick={switchToSignUp}>
                  Sign up
                </button>
              </div>
            </div>

            <div className="panel right-panel">
              <div className="content">
                <h3>Already have an account?</h3>
                <p>
                  Welcome back! Sign in to access your dashboard we'll take
                  you right where you belong.
                </p>
                <button className="btn transparent" onClick={switchToSignIn}>
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function GoogleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function SSOIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 2L2 7L12 12L22 7L12 2Z" />
      <path d="M2 17L12 22L22 17" />
      <path d="M2 12L12 17L22 12" />
    </svg>
  );
}

function AuthStyles() {
  return (
    <style>{`
      .auth-page {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
        min-height: 100vh;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 20px;
      }

      .auth-logo {
        width: 100px;
        height: 130px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 0rem;
        padding: 2px;
      }

      .auth-logo-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 50%;
      }

      .auth-error {
        width: 100%;
        max-width: 380px;
        padding: 8px 14px;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
        color: #dc2626;
        font-size: 0.8rem;
        margin-bottom: 4px;
        text-align: center;
        flex-shrink: 0;
      }

      .auth-success {
        width: 100%;
        max-width: 380px;
        padding: 8px 14px;
        background: #f0fdf4;
        border: 1px solid #bbf7d0;
        border-radius: 8px;
        color: #16a34a;
        font-size: 0.8rem;
        margin-bottom: 4px;
        text-align: center;
        flex-shrink: 0;
      }

      .forgot-link {
        background: none;
        border: none;
        color: #1e40af;
        font-size: 0.85rem;
        cursor: pointer;
        margin-bottom: 4px;
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      .forgot-link:hover {
        color: #1e3a8a;
      }

      .auth-container {
        position: relative;
        width: 100%;
        max-width: 900px;
        height: 660px;
        background: white;
        border-radius: 20px;
        box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
        overflow: hidden;
      }

      .forms-container {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }

      .signin-signup {
        position: absolute;
        top: 0;
        left: 75%;
        transform: translateX(-50%);
        width: 50%;
        height: 100%;
        transition: 1s 0.7s ease-in-out;
        display: grid;
        grid-template-columns: 1fr;
        z-index: 5;
      }

      .auth-container form {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        padding: 1.2rem 2rem;
        transition: all 0.2s 0.7s;
        overflow-y: auto;
        overflow-x: hidden;
        grid-column: 1 / 2;
        grid-row: 1 / 2;
        height: 100%;
      }

      .auth-container form.sign-up-form {
        opacity: 0;
        z-index: 1;
      }

      .auth-container form.sign-in-form {
        z-index: 2;
      }

      .auth-container .title {
        font-size: 1.7rem;
        color: #1e3a8a;
        margin-bottom: 6px;
        font-weight: 700;
      }

      .auth-container .input-field {
        max-width: 380px;
        width: 100%;
        background-color: #eff6ff;
        margin: 5px 0;
        height: 46px;
        border-radius: 46px;
        display: flex;
        align-items: center;
        padding: 0 1.2rem;
        position: relative;
        transition: 0.3s;
        border: 2px solid transparent;
        flex-shrink: 0;
      }

      .auth-container .input-field:focus-within {
        background-color: #dbeafe;
        border-color: #3b82f6;
      }

      .auth-container .input-field i {
        color: #666;
        transition: 0.5s;
        font-size: 1rem;
        margin-right: 0.5rem;
        flex-shrink: 0;
        width: 20px;
        text-align: center;
      }

      .auth-container .input-field input {
        background: none;
        outline: none;
        border: none;
        line-height: 1;
        font-weight: 500;
        font-size: 0.95rem;
        color: #333;
        width: 100%;
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .auth-container .input-field input::placeholder {
        color: #a1a1aa;
        font-weight: 400;
      }

      .auth-container .btn {
        width: 160px;
        background: linear-gradient(135deg, #1e40af, #3b82f6);
        border: none;
        outline: none;
        height: 42px;
        border-radius: 42px;
        color: #fff;
        text-transform: uppercase;
        font-weight: 600;
        margin: 6px 0;
        cursor: pointer;
        transition: 0.4s;
        font-size: 0.8rem;
        letter-spacing: 0.5px;
        flex-shrink: 0;
      }

      .auth-container .btn:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(79, 70, 229, 0.4);
      }

      .auth-container .btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .panels-container {
        position: absolute;
        height: 100%;
        width: 100%;
        top: 0;
        left: 0;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
      }

      .panel {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: space-around;
        text-align: center;
        z-index: 6;
      }

      .left-panel {
        pointer-events: all;
        padding: 3rem 17% 2rem 12%;
      }

      .right-panel {
        pointer-events: none;
        padding: 3rem 12% 2rem 17%;
      }

      .panel .content {
        color: #fff;
        transition: transform 0.9s ease-in-out;
        transition-delay: 0.6s;
      }

      .panel h3 {
        font-weight: 700;
        line-height: 1;
        font-size: 1.5rem;
        margin-bottom: 10px;
      }

      .panel p {
        font-size: 0.95rem;
        padding: 0.7rem 0;
        line-height: 1.5;
      }

      .btn.transparent {
        margin: 0;
        background: none;
        border: 2px solid #fff;
        width: 130px;
        height: 41px;
        font-weight: 600;
        font-size: 0.8rem;
      }

      .btn.transparent:hover {
        background: rgba(255, 255, 255, 0.15);
        transform: translateY(-2px);
      }

      .right-panel .content {
        transform: translateX(800px);
      }

      .auth-container.sign-up-mode:before {
        transform: translate(100%, -50%);
        right: 52%;
      }

      .auth-container.sign-up-mode .left-panel .content {
        transform: translateX(-800px);
      }

      .auth-container.sign-up-mode .signin-signup {
        left: 25%;
      }

      .auth-container form::-webkit-scrollbar {
        width: 4px;
      }

      .auth-container form::-webkit-scrollbar-thumb {
        background: #d4d4d8;
        border-radius: 4px;
      }

      .auth-container.sign-up-mode form.sign-up-form {
        opacity: 1;
        z-index: 2;
      }

      .auth-container.sign-up-mode form.sign-in-form {
        opacity: 0;
        z-index: 1;
      }

      .auth-container.sign-up-mode .right-panel .content {
        transform: translateX(0%);
      }

      .auth-container.sign-up-mode .left-panel {
        pointer-events: none;
      }

      .auth-container.sign-up-mode .right-panel {
        pointer-events: all;
      }

      .auth-container:before {
        content: "";
        position: absolute;
        height: 2000px;
        width: 2000px;
        top: -10%;
        right: 48%;
        transform: translateY(-50%);
        background: linear-gradient(-45deg, #1e40af 0%, #3b82f6 100%);
        transition: 1.8s ease-in-out;
        border-radius: 50%;
        z-index: 6;
      }

      .social-text {
        padding: 0.2rem 0;
        font-size: 0.85rem;
        color: #71717a;
        flex-shrink: 0;
      }

      .social-media {
        display: flex;
        justify-content: center;
        gap: 10px;
        flex-shrink: 0;
      }

      .social-icon {
        height: 46px;
        width: 46px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid #e4e4e7;
        border-radius: 50%;
        color: #1e40af;
        font-size: 1.2rem;
        transition: 0.3s;
        cursor: pointer;
        background: white;
      }

      .social-icon:hover:not(:disabled) {
        border-color: #3b82f6;
        transform: translateY(-3px);
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
      }

      .social-icon:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .google-signin-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        width: 100%;
        max-width: 180px;
        height: 42px;
        background: white;
        border: 2px solid #e4e4e7;
        border-radius: 42px;
        font-size: 0.85rem;
        font-weight: 500;
        color: #333;
        cursor: pointer;
        transition: all 0.3s ease;
        padding: 0 18px;
        flex-shrink: 0;
      }

      .google-signin-btn:hover:not(:disabled) {
        background: #f8f8f8;
        border-color: #d1d5db;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      .google-signin-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .google-signin-btn svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .sso-signin-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        width: 100%;
        max-width: 180px;
        height: 42px;
        background: linear-gradient(135deg, #2563eb, #3b82f6);
        border: none;
        border-radius: 42px;
        font-size: 0.85rem;
        font-weight: 500;
        color: white;
        cursor: pointer;
        transition: all 0.3s ease;
        padding: 0 18px;
        flex-shrink: 0;
      }

      .sso-signin-btn:hover:not(:disabled) {
        background: linear-gradient(135deg, #1d4ed8, #2563eb);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
      }

      .sso-signin-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .sso-signin-btn svg {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        stroke: white;
      }

      .social-media {
        display: flex;
        flex-direction: row;
        gap: 10px;
        width: 100%;
        max-width: 380px;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      /* ===== RESPONSIVE — TABLET ===== */
      @media (max-width: 870px) {
        .auth-page {
          padding: 16px;
          align-items: flex-start;
          padding-top: 2rem;
        }

        .auth-container {
          min-height: auto;
          height: auto;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
        }

        .auth-container:before {
          display: none;
        }

        .auth-container.sign-up-mode:before {
          display: none;
        }

        .panel {
          background: linear-gradient(-45deg, #1e40af 0%, #3b82f6 100%);
          border-radius: 12px;
          margin: 0 1rem 1rem;
        }

        .forms-container {
          position: relative;
          width: 100%;
          height: auto;
        }

        .signin-signup {
          position: relative;
          top: auto;
          left: auto;
          transform: none;
          width: 100%;
          transition: none;
          display: block;
        }

        .auth-container.sign-up-mode .signin-signup {
          left: auto;
        }

        .auth-container form {
          padding: 2rem 1.5rem;
          transition: none;
          height: auto;
          max-height: none;
          overflow-y: visible;
        }

        .signin-signup {
          height: auto;
        }

        .auth-container form.sign-in-form {
          display: flex;
        }

        .auth-container form.sign-up-form {
          display: none;
          opacity: 1;
        }

        .auth-container.sign-up-mode form.sign-in-form {
          display: none;
        }

        .auth-container.sign-up-mode form.sign-up-form {
          display: flex;
          opacity: 1;
        }

        .panels-container {
          position: relative;
          grid-template-columns: 1fr;
          grid-template-rows: auto;
          height: auto;
        }

        .panel {
          flex-direction: column;
          padding: 1.5rem 2rem;
        }

        .left-panel,
        .right-panel {
          pointer-events: all;
        }

        .left-panel {
          display: flex;
        }

        .right-panel {
          display: none;
        }

        .auth-container.sign-up-mode .left-panel {
          display: none;
        }

        .auth-container.sign-up-mode .right-panel {
          display: flex;
        }

        .panel .content {
          transform: none !important;
          padding: 0;
          text-align: center;
        }

        .panel h3 {
          font-size: 1.2rem;
        }

        .panel p {
          font-size: 0.85rem;
          padding: 0.4rem 0;
        }

        .btn.transparent {
          width: 120px;
          height: 38px;
          font-size: 0.75rem;
        }

        .auth-container .title {
          font-size: 1.6rem;
        }

        .auth-container .input-field {
          height: 48px;
          margin: 6px 0;
        }

        .auth-container .input-field i {
          line-height: 48px;
        }

        .auth-container .input-field input {
          font-size: 0.9rem;
        }

        .auth-container .btn {
          height: 44px;
          font-size: 0.8rem;
        }

        .google-signin-btn,
        .sso-signin-btn {
          height: 44px;
          font-size: 0.9rem;
        }
      }

      /* ===== RESPONSIVE — MOBILE ===== */
      @media (max-width: 570px) {
        .auth-page {
          padding: 12px;
          padding-top: 1rem;
        }

        .auth-container {
          border-radius: 12px;
        }

        .auth-container form {
          padding: 1.5rem 1.2rem;
        }

        .auth-container .title {
          font-size: 1.4rem;
          margin-bottom: 6px;
        }

        .auth-logo {
          width: 44px;
          height: 44px;
          margin-bottom: 8px;
        }

        .auth-container .input-field {
          height: 44px;
          margin: 5px 0;
          padding: 0 1rem;
        }

        .auth-container .input-field i {
          display: none;
        }

        .auth-container .input-field input {
          font-size: 0.85rem;
        }

        .auth-container .btn {
          width: 140px;
          height: 42px;
          font-size: 0.75rem;
        }

        .google-signin-btn,
        .sso-signin-btn {
          height: 42px;
          font-size: 0.85rem;
          padding: 0 16px;
        }

        .social-text {
          font-size: 0.8rem;
          padding: 0.3rem 0;
        }

        .auth-error,
        .auth-success {
          font-size: 0.8rem;
          padding: 8px 12px;
        }

        .forgot-link {
          font-size: 0.8rem;
        }

        .panel {
          padding: 1rem 1.2rem;
        }

        .panel h3 {
          font-size: 1.1rem;
        }

        .panel p {
          font-size: 0.8rem;
        }

        .btn.transparent {
          width: 100px;
          height: 34px;
          font-size: 0.7rem;
        }
      }
    `}</style>
  );
}
